import React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ButtonBase from '@mui/material/ButtonBase';

import { useAuth } from '../auth';
import { CustomerSelector } from './ParcelInvoiceManagement/CustomerSelector';

const HEADER_GRADIENT = 'linear-gradient(180deg, #1e3a8a 0%, #1d4ed8 100%)';

const getInitials = (name?: string) => {
  if (!name) return '?';
  const cleaned = name.replace(/[^a-zA-Z ]/g, ' ').trim();
  if (!cleaned) return name.slice(0, 2).toUpperCase();
  const parts = cleaned.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const Header = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const auth = useAuth();

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const initials = getInitials(auth.user?.userName);

  return (
    <AppBar
      position="sticky"
      sx={{
        background: HEADER_GRADIENT,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.15)',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 60, sm: 60 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <CustomerSelector variant="dark" />
        </Box>

        {auth.user ? (
          <>
            <ButtonBase
              onClick={handleAvatarClick}
              aria-label={`account of ${auth.user?.userName}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                pl: 1.75,
                pr: 1,
                py: 0.75,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.10)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'white',
                fontSize: 13,
                transition: 'background 0.15s ease',
                '&:hover': { background: 'rgba(255,255,255,0.18)' },
              }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: 999,
                  background: 'white',
                  color: 'primary.main',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 600,
                  fontSize: 11,
                }}
              >
                {initials}
              </Box>
              <Typography component="span" sx={{ fontSize: 13 }}>
                {auth.user?.userName}
              </Typography>
              <ExpandMoreIcon sx={{ fontSize: 18, opacity: 0.85 }} />
            </ButtonBase>
            <Menu
              id="avatar-menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 180,
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                  border: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <MenuItem onClick={auth.actions.logout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </>
        ) : (
          <IconButton
            size="large"
            aria-label="login"
            aria-haspopup="false"
            onClick={auth.actions?.login}
            color="inherit"
          >
            <LoginIcon />
            <Typography sx={{ ml: '10px' }}>Login</Typography>
          </IconButton>
        )}
      </Toolbar>
    </AppBar>
  );
};
